// src/features/users/components/UserManagement/UserFormCard/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createUser, updateUser } from '../../../../../api';
import Button from '../../../../../shared/components/ui/Button';
import styles from './UserFormCard.module.css';

const ROLE_OPTIONS = [
  { value: 'admin',        label: 'مدير'     },
  { value: 'dept_manager', label: 'مدير قسم' },
  { value: 'employee',     label: 'موظف'      },
];

const ROLE_DISPLAY_TO_KEY = {
  'مدير':     'admin',
  'مدير قسم': 'dept_manager',
  'موظف':     'employee',
};

const initialState = {
  username: '', password: '', first_name: '', last_name: '',
  email: '', role: 'employee', department_ids: [],
};

function UserFormCard({ editingUser, departments, onSaveSuccess, onCancelEdit }) {
  const [formData,   setFormData]   = useState(initialState);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingUser) {
      const roleKey = editingUser.role_key || ROLE_DISPLAY_TO_KEY[editingUser.role] || 'employee';
      setFormData({
        username:       editingUser.username,
        password:       '',
        first_name:     editingUser.first_name  || '',
        last_name:      editingUser.last_name   || '',
        email:          editingUser.email       || '',
        role:           roleKey,
        department_ids: (editingUser.departments || []).map(d => d.id),
      });
      setAvatarPreview(editingUser.avatar || null);
    } else {
      setFormData(initialState);
      setAvatarPreview(null);
    }
    setAvatarFile(null);
    setError('');
    setSuccess('');
  }, [editingUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = (deptId) => {
    setFormData(prev => ({
      ...prev,
      department_ids: prev.department_ids.includes(deptId)
        ? prev.department_ids.filter(id => id !== deptId)
        : [...prev.department_ids, deptId],
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);

    try {
      const fd = new FormData();
      if (formData.role === 'dept_manager' && formData.department_ids.length === 0) {
        setError('مدير القسم يجب أن يكون مرتبطاً بقسم واحد على الأقل.');
        setSubmitting(false); return;
      }
      
      fd.append('first_name', formData.first_name);
      fd.append('last_name', formData.last_name);
      fd.append('email', formData.email);
      fd.append('role', formData.role);
      formData.department_ids.forEach(id => fd.append('department_ids', id));
      if (avatarFile) fd.append('avatar', avatarFile);

      if (editingUser) {
        if (formData.password) fd.append('password', formData.password);
        await updateUser(editingUser.id, fd, { headers: { 'Content-Type': 'multipart/form-data' }});
        setSuccess(`تم تحديث المستخدم "${editingUser.username}" بنجاح!`);
      } else {
        if (!formData.username) { setError('اسم المستخدم حقل إلزامي.'); setSubmitting(false); return; }
        if (!formData.password) { setError('كلمة المرور حقل إلزامي.');  setSubmitting(false); return; }
        fd.append('username', formData.username);
        fd.append('password', formData.password);
        await createUser(fd, { headers: { 'Content-Type': 'multipart/form-data' }});
        setSuccess(`تم إنشاء المستخدم "${formData.username}" بنجاح!`);
      }
      onSaveSuccess();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        setError(Object.entries(data).map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(' ') : m}`).join(' | ') || 'حدث خطأ غير متوقع.');
      } else {
        setError(err.message || 'حدث خطأ في الاتصال بالخادم.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>
        {editingUser ? `تعديل: ${editingUser.username}` : 'إضافة مستخدم جديد'}
      </h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        
        {/* رفع الصورة */}
        <div className={styles.avatarSection}>
          <div className={styles.avatarPreview} onClick={() => fileInputRef.current?.click()}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar" />
            ) : (
              <span className={styles.avatarPlaceholder}>👤</span>
            )}
          </div>
          <div className={styles.avatarInfo}>
            <p>الصورة الشخصية</p>
            <small>JPG أو PNG — حجم أقصى 2MB</small>
            <div className={styles.avatarActions}>
              <Button variant="info" size="sm" type="button" onClick={() => fileInputRef.current?.click()}>
                {avatarPreview ? 'تغيير الصورة' : 'رفع صورة'}
              </Button>
              {avatarPreview && (
                <Button variant="danger" size="sm" type="button" onClick={handleRemoveAvatar}>
                  حذف
                </Button>
              )}
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleAvatarChange} />
        </div>

        {/* الحقول */}
        <div className={styles.inputGrid}>
          <input name="username" value={formData.username} onChange={handleChange} placeholder="اسم المستخدم *" required disabled={!!editingUser} className="form-input" />
          <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder={editingUser ? 'كلمة مرور جديدة (اتركه فارغاً)' : 'كلمة المرور *'} required={!editingUser} className="form-input" />
          <input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="الاسم الأول" className="form-input" />
          <input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="الاسم الأخير" className="form-input" />
          <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="البريد الإلكتروني" className="form-input" style={{ gridColumn: '1 / -1' }} />
          <select name="role" value={formData.role} onChange={handleChange} required className="form-input" style={{ gridColumn: '1 / -1' }}>
            {ROLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        {/* الأقسام (تظهر فقط لمدير القسم) */}
        {formData.role === 'dept_manager' && (
          <fieldset className={styles.deptFieldset}>
            <legend className={styles.deptLegend}>الأقسام المصرح بها *</legend>
            {departments.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>لا توجد أقسام.</p>
            ) : (
              <div className={styles.deptGrid}>
                {departments.map(dept => (
                  <label key={dept.id} className={styles.deptLabel}>
                    <input type="checkbox" checked={formData.department_ids.includes(dept.id)} onChange={() => handleDepartmentChange(dept.id)} />
                    {dept.name}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        {/* التنبيهات */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* الأزرار */}
        <div className={styles.formActions}>
          {editingUser && (
            <Button variant="secondary" type="button" onClick={onCancelEdit} disabled={submitting}>
              إلغاء التعديل
            </Button>
          )}
          <Button variant="primary" type="submit" loading={submitting}>
            {editingUser ? 'حفظ التغييرات' : '+ إنشاء المستخدم'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default UserFormCard;